function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}
var dispatchCustomEvent = function dispatchCustomEvent(eventName) {
  var data = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var detail = {
    detail: data
  };
  var event = new CustomEvent(eventName, data ? detail : null);
  document.dispatchEvent(event);
};
/**
 *  @function publishStandardEvent
 *  Guarded dispatch of a Shopify standard event (shopify:* DOM events).
 *  No-op when the standard-events module is absent; an analytics error
 *  must never reach a commerce code path.
 */
var publishStandardEvent = function (eventName, payload, target) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !SE[eventName]) return;
  try {
    (target || document).dispatchEvent(new SE[eventName](payload || {}));
  } catch (e) {
    console.error('Standard event failed:', eventName, e);
  }
};
/**
 *  @function standardEventCart
 *  AJAX cart JSON (a /cart/*.js response) → Storefront-shape cart, or null.
 */
var standardEventCart = function (cart) {
  const SE = window.Shopify && window.Shopify.StandardEvents;
  if (!SE || !cart || !cart.token || !cart.items) return null;
  try {
    return SE.CartViewEvent.createCartFromAjaxResponse(cart);
  } catch (e) {
    return null;
  }
};
/**
 *  ThemeFocusTrap (BFSG/a11y — angelehnt an v11 OverlayManager.trapFocus)
 *  Zentraler Tab-Trap: das aktive Overlay wird zur Laufzeit ermittelt, damit
 *  alle Panel-Typen (Side-Panels, Facet-Drawer, Cart-Drawer, Compare, Pickup)
 *  ohne Einzelverdrahtung abgedeckt sind. capture/restore an den Oeffnungs-/
 *  Schliessstellen gibt den Fokus an den Ausloeser zurueck.
 */
const ThemeFocusTrap = {
  openers: new WeakMap(),
  focusableSelector: 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  currentPanel() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.isOpen) return drawer;
    const panels = document.querySelectorAll('.side-panel.active:not([inert])');
    return panels.length ? panels[panels.length - 1] : null;
  },
  handleKeydown(e) {
    if (e.key !== 'Tab') return;
    const panel = this.currentPanel();
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll(this.focusableSelector)).filter((el) => el.getClientRects().length);
    if (!focusable.length) {
      e.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const inside = panel.contains(document.activeElement);
    if (e.shiftKey) {
      if (!inside || document.activeElement === first) {
        last.focus();
        e.preventDefault();
      }
    } else if (!inside || document.activeElement === last) {
      first.focus();
      e.preventDefault();
    }
  },
  capture(panel) {
    if (panel) this.openers.set(panel, document.activeElement);
  },
  restore(panel) {
    if (!panel) return;
    const opener = this.openers.get(panel);
    this.openers.delete(panel);
    if (opener && !this.currentPanel() && document.contains(opener)) {
      opener.focus();
    }
  }
};
document.addEventListener('keydown', (e) => ThemeFocusTrap.handleKeydown(e));
window.ThemeFocusTrap = ThemeFocusTrap;

window.recentlyViewedIds = [];

if (!customElements.get('quantity-selector')) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.input = this.querySelector('.qty');
      this.step = parseFloat(this.input.getAttribute('step')) || 1;
      this.changeEvent = new Event('change', { bubbles: true });

      // Create buttons
      this.subtract = this.querySelector('.minus');
      this.add = this.querySelector('.plus');

      // Add functionality to buttons
      this.subtract.addEventListener('click', () => this.changeQuantity(-1 * this.step));
      this.add.addEventListener('click', () => this.changeQuantity(1 * this.step));

      this.classList.add('buttons_added');
      this.validateQtyRules();
    }
    changeQuantity(change) {
      let quantity = parseInt(this.input.value) || 0;

      // Update quantity
      const newValue = Math.max(quantity + change, 1);

      // Check min and max values
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || Infinity;
      if (newValue < min || newValue > max) return;

      // Update input value
      this.input.value = newValue;

      // Dispatch change event
      this.input.dispatchEvent(this.changeEvent);

      // Update button states
      this.validateQtyRules();
    }

    validateQtyRules() {
      const value = parseInt(this.input.value) || 0;
      const min = parseInt(this.input.min) || 1;
      const max = parseInt(this.input.max) || Infinity;

      this.subtract.classList.toggle('disabled', value <= min);
      this.add.classList.toggle('disabled', value >= max);
    }
  }

  customElements.define('quantity-selector', QuantityInput);
}

/**
 *  @class
 *  @function ProductCard
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.swatches = this.querySelector('.product-card-swatches');
      this.image = this.querySelector('.product-card-image-link .product-primary-image');
      this.additional_images = this.querySelectorAll('.product-secondary-image');
      this.additional_images_nav = this.querySelectorAll('.product-secondary-images-nav li');
      this.quick_add = this.querySelector('.product-card--add-to-cart-button-simple');

      if (this.swatches) {
        this.enableSwatches();
      }
      if (this.additional_images.length) {
        this.enableAdditionalImages();
      }
      if (this.quick_add) {
        this.enableQuickAdd();
      }
      if (this.querySelector('[data-card-slider]')) {
        this.enableCardSlider();
      }
    }
    enableCardSlider() {
      // Guard: connectedCallback kann bei AJAX-Verschieben mehrfach laufen
      if (this.dataset.sliderInit) return;
      this.dataset.sliderInit = 'true';
      const track = this.querySelector('[data-card-slider]');
      const prev = this.querySelector('.product-card-slider-arrow--prev');
      const next = this.querySelector('.product-card-slider-arrow--next');
      const dots = this.querySelectorAll('.product-card-slider-dots span');
      const slides = track.querySelectorAll('.product-card-slide');
      this.card_slider = track;
      let ticking = false;
      let loaded = false;

      const update = () => {
        ticking = false;
        const w = track.clientWidth || 1;
        const index = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w)));
        dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        if (prev) prev.classList.toggle('is-hidden', index === 0);
        if (next) next.classList.toggle('is-hidden', index === slides.length - 1);
      };
      const loadImages = () => {
        if (loaded) return;
        loaded = true;
        if (!window.lazySizes || !lazySizes.loader) return;
        track.querySelectorAll('img.lazyload').forEach((img) => lazySizes.loader.unveil(img));
      };
      const go = (dir) => (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        loadImages();
        track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
      };

      if (prev) prev.addEventListener('click', go(-1));
      if (next) next.addEventListener('click', go(1));
      track.addEventListener('scroll', () => {
        loadImages();
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }, { passive: true });
      this.addEventListener('pointerenter', loadImages, { once: true, passive: true });
      this.addEventListener('touchstart', loadImages, { once: true, passive: true });
    }
    enableAdditionalImages() {
      let image_length = this.additional_images.length;
      let images = this.additional_images;
      let nav = this.additional_images_nav;
      let image_container = this.querySelector('.product-card-image');
      let currentSel = -1;
      const mousemove = function (e) {
        const sel = Math.floor(e.offsetX / this.getBoundingClientRect().width * image_length);
        if (sel === currentSel) return;
        if (currentSel >= 0) {
          images[currentSel].classList.remove('hover');
          if (nav.length) nav[currentSel].classList.remove('active');
        }
        currentSel = sel;
        if (images[sel]) {
          images[sel].classList.add('hover');
          if (nav.length) nav[sel].classList.add('active');
        }
      };
      const mouseleave = function () {
        if (currentSel >= 0) {
          images[currentSel].classList.remove('hover');
          if (nav.length) nav[currentSel].classList.remove('active');
          currentSel = -1;
        }
      };
      if (image_container) {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const mobileSwipeEnabled = window.theme && theme.settings && theme.settings.mobile_swipe;

        if (isTouchDevice && mobileSwipeEnabled) {
          let touchStartX = 0;
          let touchStartY = 0;
          let isSwiping = false;
          let swipeOccurred = false;
          let currentIndex = -1;
          const imageLink = this.querySelector('.product-card-image-link');

          const showImageAtIndex = (index) => {
            images.forEach(img => img.classList.remove('hover'));
            nav.forEach(dot => dot.classList.remove('active'));
            if (index >= 0 && images[index]) {
              images[index].classList.add('hover');
              if (nav.length) nav[index].classList.add('active');
            }
          };

          image_container.addEventListener('touchstart', (e) => {
            const touch = e.touches[0];
            touchStartX = touch.clientX;
            touchStartY = touch.clientY;
            isSwiping = false;
          }, { passive: true });

          image_container.addEventListener('touchmove', (e) => {
            const touch = e.touches[0];
            const deltaX = touch.clientX - touchStartX;
            const deltaY = touch.clientY - touchStartY;
            if (!isSwiping && Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY) * 1.5) {
              isSwiping = true;
            }
          }, { passive: true });

          image_container.addEventListener('touchend', (e) => {
            if (!isSwiping) return;
            const deltaX = e.changedTouches[0].clientX - touchStartX;
            if (Math.abs(deltaX) >= 30) {
              if (deltaX < 0 && currentIndex < image_length - 1) {
                currentIndex++;
              } else if (deltaX > 0 && currentIndex > -1) {
                currentIndex--;
              }
              showImageAtIndex(currentIndex);
            }
            swipeOccurred = true;
            setTimeout(() => { swipeOccurred = false; }, 300);
          }, { passive: true });

          if (imageLink) {
            imageLink.addEventListener('click', (e) => {
              if (swipeOccurred) {
                e.preventDefault();
                e.stopPropagation();
              }
            }, { capture: true });
          }
        } else {
          image_container.addEventListener('touchstart', mousemove, { passive: true });
          image_container.addEventListener('touchmove', mousemove, { passive: true });
          image_container.addEventListener('touchend', mouseleave, { passive: true });
        }

        image_container.addEventListener('mouseenter', mousemove, { passive: true });
        image_container.addEventListener('mousemove', mousemove, { passive: true });
        image_container.addEventListener('mouseleave', mouseleave, { passive: true });
      }

      window.addEventListener('load', () => {
        images.forEach(function (image) {
          lazySizes.loader.unveil(image);
        });
      });

    }
    enableSwatches() {
      let swatch_list = this.swatches.querySelectorAll('.product-card-swatch');
      this.color_index = this.swatches.dataset.index;
      this.org_srcset = this.image ? this.image.dataset.srcset : '';

      this.addEventListener('change', this.onSwatchChange.bind(this));

      window.addEventListener('load', () => {
        swatch_list.forEach((swatch) => {
          let image = new Image();
          image.srcset = swatch.dataset.srcset;
          lazySizes.loader.unveil(image);
        });
      });
      swatch_list.forEach((swatch) => {
        swatch.addEventListener('click', this.onSwatchClick);
        swatch.addEventListener('mouseover', this.onSwatchHover);
      });
    }
    onSwatchClick() {
      window.location.href = this.dataset.href;
    }
    onSwatchHover() {
      let swatch_input = this.previousElementSibling;
      swatch_input.checked = true;
      swatch_input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    onSwatchChange(evt) {
      if (this.card_slider) {
        this.card_slider.scrollLeft = 0;
      }
      if (this.image) {
        if (evt.target.dataset.srcset) {
          this.image.setAttribute('srcset', evt.target.dataset.srcset);
        } else {
          this.image.setAttribute('srcset', this.org_srcset);
        }
      }
    }
    enableQuickAdd() {
      this.quick_add.addEventListener('click', this.quickAdd.bind(this));

    }
    quickAdd(evt) {
      evt.preventDefault();
      if (this.quick_add.disabled) {
        return;
      }
      this.quick_add.disabled = true;
      this.quick_add.classList.add('loading');
      this.quick_add.setAttribute('aria-disabled', true);

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      let formData = new FormData();

      formData.append('id', this.quick_add.dataset.productId);
      formData.append('quantity', 1);
      formData.append('sections', this.getSectionsToRender().map((section) => section.section));
      formData.append('sections_url', window.location.pathname);

      config.body = formData;

      fetch(`${theme.routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            return;
          }
          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', {
            product: response.hasOwnProperty('items') ? response.items[0] : response
          });
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.quick_add.disabled = false;
          this.quick_add.classList.remove('loading');
          this.quick_add.removeAttribute('aria-disabled');
        });

      return false;
    }
    getSectionsToRender() {
      return [{
        id: 'Cart',
        section: 'main-cart',
        selector: '.thb-cart-form'
      },
      {
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-secondary-area--item-count'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-secondary-area--item-amount'
      }];
    }
    renderContents(parsedState) {
      const parsed = {};
      this.getSectionsToRender().forEach((section => {
        if (!document.getElementById(section.id)) {
          return;
        }
        if (!parsed[section.section]) { parsed[section.section] = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html'); }
        const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = parsed[section.section].querySelector(section.selector)?.innerHTML;
      }));

      if (document.getElementById('Cart-Drawer')) {
        document.getElementById('Cart-Drawer').open();
      }
    }
  }
  customElements.define('product-card', ProductCard);
}

/**
 *  @class
 *  @function PanelClose
 */
if (!customElements.get('side-panel-close')) {
  class PanelClose extends HTMLElement {

    constructor() {
      super();
      let cc = document.querySelector('.click-capture');

      // BFSG: Close-Element tastaturbedienbar machen (war reines Klick-Custom-Element)
      if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
      this.setAttribute('role', 'button');
      if (!this.hasAttribute('aria-label') && window.theme?.strings?.close) {
        this.setAttribute('aria-label', theme.strings.close);
      }

      // Add functionality to buttons
      this.addEventListener('click', (e) => this.close_panel(e));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          this.close_panel(e);
        }
      });
      document.addEventListener('panel:close', (e) => {
        let panel = document.querySelectorAll('.side-panel.active');
        if (panel.length) {
          this.close_panel(e, panel[0]);
        }
      });
      cc.addEventListener('click', (e) => {
        let panel = document.querySelectorAll('.side-panel.active');
        if (panel.length) {
          this.close_panel(e, panel[0]);
        }
      });
    }

    close_panel(e, panel) {
      if (e) {
        e.preventDefault();
      }
      if (!panel) {
        panel = e?.target.closest('.side-panel');

        if (!panel) {
          return;
        }
      }
      panel.classList.remove('active');
      document.body.classList.remove('open-cc');
      panel.setAttribute('inert', '');
      ThemeFocusTrap.restore(panel);
    }
  }
  customElements.define('side-panel-close', PanelClose);

  document.addEventListener('keyup', (e) => {
    if (e.code) {
      if (e.code.toUpperCase() === 'ESCAPE') {
        dispatchCustomEvent('panel:close');
      }
    }
  });
}
/**
 *  @class
 *  @function CartDrawer
 */
if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      this.isOpen = false;
      this.button = document.getElementById('cart-drawer-toggle');

      this.debouncedOnChange = debounce((event) => {
        this.onChange(event);
      }, 300);

      this._onCartRefresh = () => this.refresh();
      this._onWindowClick = this.onWindowClick.bind(this);
      this._onKeyUp = (e) => {
        if (e.code && e.code.toUpperCase() === 'ESCAPE') {
          this.close();
        }
      };
      this._onButtonClick = (e) => {
        e.preventDefault();
        this.isOpen ? this.close() : this.open();
      };

      document.addEventListener('cart:refresh', this._onCartRefresh);
      this.addEventListener('change', this.debouncedOnChange.bind(this));
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('.remove');
        if (remove) {
          event.preventDefault();
          this.updateQuantity(remove.dataset.index, '0');
        }
      });
      document.addEventListener('click', this._onWindowClick);
      this.button.addEventListener('click', this._onButtonClick);
      document.addEventListener('keyup', this._onKeyUp);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:refresh', this._onCartRefresh);
      document.removeEventListener('click', this._onWindowClick);
      document.removeEventListener('keyup', this._onKeyUp);
      this.button?.removeEventListener('click', this._onButtonClick);
    }

    onWindowClick(event) {
      if (this.isOpen && !this.contains(event.target) && !this.button.contains(event.target)) {
        this.close();
      }
    }
    open() {
      ThemeFocusTrap.capture(this);
      this.removeAttribute('inert');
      this.focus();
      this.isOpen = true;
      dispatchCustomEvent('cart-drawer:open');
    }
    close() {
      this.setAttribute('inert', '');
      this.isOpen = false;
      ThemeFocusTrap.restore(this);
    }
    removeProductEvent() {
      // Backward-compat stub: event delegation handles this now
    }
    onChange(event) {
      if (event.target.classList.contains('qty')) {
        this.updateQuantity(event.target.dataset.index, event.target.value);
      }
    }
    getSectionsToRender() {
      return [{
        id: 'Cart-Drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer--inner'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-secondary-area--item-count'
      },
      {
        id: 'cart-drawer-toggle',
        section: 'cart-bubble',
        selector: '.thb-secondary-area--item-amount'
      }];
    }
    updateQuantity(line, quantity) {
      this.querySelector(`#CartDrawerItem-${line}`)?.classList.add('thb-loading');
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname
      });

      dispatchCustomEvent('line-item:change:start', {
        quantity: quantity
      });

      fetch(`${theme.routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body
      })
        .then((response) => response.json())
        .then((parsedState) => {
          if (parsedState.sections) {
            const parsed = {};
            this.getSectionsToRender().forEach((section => {
              if (!parsed[section.section]) { parsed[section.section] = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html'); }
              const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
              elementToReplace.innerHTML = parsed[section.section].querySelector(section.selector)?.innerHTML;
            }));
          }

          dispatchCustomEvent('line-item:change:end', {
            quantity: quantity,
            cart: parsedState
          });

          if (this.querySelector(`#CartDrawerItem-${line}`)) {
            this.querySelector(`#CartDrawerItem-${line}`).classList.remove('thb-loading');
          }
        });
    }
    refresh() {
      let sections = 'cart-drawer,cart-bubble';
      fetch(`${window.location.pathname}?sections=${sections}`)
        .then((response) => response.json())
        .then((parsedState) => {
          const parsed = {};
          this.getSectionsToRender().forEach((section => {
            if (!parsed[section.section]) { parsed[section.section] = new DOMParser().parseFromString(parsedState[section.section], 'text/html'); }
            const elementToReplace = document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
            elementToReplace.innerHTML = parsed[section.section].querySelector(section.selector)?.innerHTML;
          }));

        });
    }
  }
  customElements.define('cart-drawer', CartDrawer);
}

/**
 *  @class
 *  @function LocalizationForm
 */
if (!customElements.get('localization-form')) {
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.inputs = this.form.querySelectorAll('[name="locale_code"], [name="country_code"]');
      this.debouncedOnSubmit = debounce((event) => {
        this.onSubmitHandler(event);
      }, 200);
      this.inputs.forEach(item => item.addEventListener('change', this.debouncedOnSubmit.bind(this)));
    }
    onSubmitHandler() {
      this.form.submit();
    }
  }
  customElements.define('localization-form', LocalizationForm);
}

/**
 *  @class
 *  @function ResizeSelect
 */
if (!customElements.get('resize-select')) {
  class ResizeSelect extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.select = this.querySelector('select');
      this.addEventListeners();
    }
    addEventListeners() {
      this.addEventListener('change', this.resizeSelect.bind(this));
      window.addEventListener('load', this.resizeSelect.bind(this));
      const details = this.closest('details');
      if (details) {
        details.addEventListener('toggle', () => {
          if (details.open) this.resizeSelect();
        });
      }
      this.resizeSelect();
    }
    resizeSelect() {
      let tempOption = document.createElement('option');
      tempOption.textContent = this.select.selectedOptions[0].textContent;

      let tempSelect = document.createElement('select');
      tempSelect.style.cssText = 'visibility:hidden;position:fixed;pointer-events:none';
      tempSelect.appendChild(tempOption);
      this.after(tempSelect);

      requestAnimationFrame(() => {
        if (tempSelect.clientWidth > 0) {
          this.select.style.width = `${tempSelect.clientWidth + 10}px`;
        }
        tempSelect.remove();
      });
    }
  }
  customElements.define('resize-select', ResizeSelect);
}

/**
 *  @class
 *  @function QuickView
 */
const quickViewCache = new Map();

if (!customElements.get('quick-view')) {
  class QuickView extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.drawer = document.getElementById('Product-Modal');

      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('keypress', this.handleClick.bind(this));
    }
    handleClick(e) {
      if (e.key && e.key !== 'Enter') {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      // stopPropagation unterbindet auch CartDrawer.onWindowClick — offenen Drawer
      // deshalb explizit schliessen, bevor das Quick-View-Modal darueber aufgeht.
      const openCartDrawer = document.querySelector('cart-drawer');
      if (openCartDrawer && openCartDrawer.isOpen) {
        openCartDrawer.close();
      }
      let productHandle = this.dataset.productHandle,
        href = `${theme.routes.root_url}/products/${productHandle}?view=quick-view`;

      // remove double `/` in case shop might have /en or language in URL
      href = href.replace('//', '/');
      if (!href || !productHandle) {
        return;
      }

      // Serve from cache if available
      if (quickViewCache.has(productHandle)) {
        this.renderQuickview(quickViewCache.get(productHandle), href, productHandle);
        return;
      }

      if (this.classList.contains('loading')) {
        return;
      }

      // Abort any in-flight request
      if (QuickView.activeController) {
        QuickView.activeController.abort();
      }

      this.classList.add('loading');

      let controller = new AbortController();
      QuickView.activeController = controller;

      fetch(href, { method: 'GET', signal: controller.signal })
        .then((response) => {
          return response.text();
        })
        .then(text => {
          QuickView.activeController = null;
          this.classList.remove('loading');
          const sectionInnerHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('#Product-Modal-Content').innerHTML;
          quickViewCache.set(productHandle, sectionInnerHTML);
          this.renderQuickview(sectionInnerHTML, href, productHandle);
        })
        .catch((err) => {
          this.classList.remove('loading');
          if (err.name === 'AbortError') {
            return;
          }
        });
    }
    _reloadScripts(container) {
      container.querySelectorAll('script').forEach((oldScript) => {
        let script = document.createElement('script');
        if (oldScript.src) {
          script.src = oldScript.src;
        } else {
          script.textContent = oldScript.textContent;
        }
        oldScript.parentNode.replaceChild(script, oldScript);
      });
    }
    renderQuickview(sectionInnerHTML, href, productHandle) {
      if (sectionInnerHTML) {
        let content = this.drawer.querySelector('#Product-Modal-Content');
        content.innerHTML = sectionInnerHTML;

        // A11y: Dialog nach dem geladenen Produkt benennen (statischer Fallback = 'Schnellansicht' aus product-drawer.liquid)
        const dialog = this.drawer.querySelector('.product-modal__content');
        const titleEl = content.querySelector('.product--title, .product-title, h1, h2');
        if (dialog && titleEl && titleEl.textContent.trim()) {
          dialog.setAttribute('aria-label', titleEl.textContent.trim());
        }

        this._reloadScripts(content);

        requestAnimationFrame(() => {
          if (Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }
          if (window.ProductModel) {
            window.ProductModel.loadShopifyXR();
          }
        });

        this.drawer.setAttribute('open', '');
        this.drawer.querySelector('.product-modal__toggle').focus();

        setTimeout(() => {
          let slider = this.drawer.querySelector('#Product-Slider');

          // Guard: product.js wird bei Quick-View auf Nicht-PDPs erst nachgeladen -> Element evtl. noch nicht upgraded
          if (slider && typeof slider.reInit === 'function') {
            slider.reInit();
          }
          window.dispatchEvent(new Event('resize'));
        }, 100);

        dispatchCustomEvent('quick-view:open', {
          productUrl: href,
          productHandle: productHandle
        });
        addIdToRecentlyViewed(productHandle);
      }
    }
  }
  QuickView.activeController = null;
  customElements.define('quick-view', QuickView);
}

/**
 *  @class
 *  @function ThemeScroll
 */
if (!customElements.get('theme-scroll')) {
  class ThemeScroll extends HTMLElement {
    constructor() {
      super();

      this.viewport = this.querySelector('.theme-scroll--inner');
      this.slideWidth = 274;
      this.slidesToScroll = 1;
    }
    connectedCallback() {
      // OverlayScrollbars erst initialisieren, wenn der Slider in Viewport-Naehe kommt: jede Init und
      // jedes Folge-Update (img load) erzwingt Style/Layout ueber das ganze Dokument, auf der Startseite
      // liegen alle Instanzen below the fold. Vor der Init ist der Slider per CSS nativ scrollbar
      // ([data-overlayscrollbars-initialize] -> overflow:auto, Scrollbar versteckt).
      if ('IntersectionObserver' in window) {
        // Erst nach window.load beobachten: Section-CSS wird deferred geladen (media=print/onload),
        // davor ist das Dokument kompakt und JEDER Slider laege "in Viewport-Naehe".
        const observeWhenReady = () => {
          if (this._osInitialized || !this.isConnected) { return; }
          // Zoom-stabile "Naehe" (2 Bildschirmhoehen) fuer Observer-Plausibilitaet und Scroll-Fallback
          const nearPx = Math.max(window.screen && window.screen.height || 0, 600) * 2;
          this._osObserver = new IntersectionObserver((entries) => {
            // Plausibilitaetscheck: direkt nach load kann der IO-Root transient ein Vielfaches des
            // Viewports sein (Mobil), dann meldet er alle Slider als "nah". screen.height ist zoom-stabil.
            if (entries.some((entry) => entry.isIntersecting && entry.boundingClientRect.top < nearPx && entry.boundingClientRect.bottom > -nearPx)) {
              this._osObserver.disconnect();
              this._osObserver = null;
              this.initScrollbars();
            }
          }, { rootMargin: '100% 0px' });
          this._osObserver.observe(this);
          // Fallback: nach dem transienten Erst-Callback feuert der Observer auf manchen Mobil-Browsern
          // nicht erneut -> zusaetzlich rAF-gedrosselter Rect-Check beim Scrollen (entfernt sich nach Init).
          this._osScrollCheck = () => {
            if (this._osScrollTick) { return; }
            this._osScrollTick = true;
            requestAnimationFrame(() => {
              this._osScrollTick = false;
              if (this._osInitialized) { return; }
              const rect = this.getBoundingClientRect();
              if (rect.top < nearPx && rect.bottom > -nearPx) { this.initScrollbars(); }
            });
          };
          window.addEventListener('scroll', this._osScrollCheck, { passive: true });
        };
        if (document.readyState === 'complete') {
          observeWhenReady();
        } else {
          window.addEventListener('load', observeWhenReady, { once: true });
        }
      } else {
        this.initScrollbars();
      }

      this.init();
    }
    disconnectedCallback() {
      if (this._osObserver) {
        this._osObserver.disconnect();
        this._osObserver = null;
      }
      if (this._osScrollCheck) {
        window.removeEventListener('scroll', this._osScrollCheck);
        this._osScrollCheck = null;
      }
    }
    initScrollbars() {
      if (this._osInitialized || !window.OverlayScrollbarsGlobal) {
        return;
      }
      this._osInitialized = true;
      if (this._osScrollCheck) {
        window.removeEventListener('scroll', this._osScrollCheck);
        this._osScrollCheck = null;
      }
      if (this._osObserver) {
        this._osObserver.disconnect();
        this._osObserver = null;
      }
      window.OverlayScrollbarsGlobal.OverlayScrollbars({
        target: this,
        overflow: {
          x: 'scroll',
          y: 'hidden'
        },
        elements: {
          viewport: this.viewport,
        }
      }, {});
    }
    init() {
      this.reserveScrollbarSpace();
      this.setSlides();
      this.reachedEnd = false;

      if (this.slides.length < 1) {
        return;
      }
      this.nav = this.querySelector('.theme-scroll-nav');
      this.navPrev = this.querySelector('.flickity-prev');
      this.navNext = this.querySelector('.flickity-next');

      // Use entry.borderBoxSize to get the slide width without reading offsetLeft
      this.resizeObserver = new ResizeObserver((entries) => {
        if (entries[0]) {
          this.slideWidth = entries[0].borderBoxSize[0].inlineSize;
        }
        const scrollLeft = this.viewport.scrollLeft;
        this.reachedEnd = this.viewport.scrollWidth - this.viewport.offsetWidth <= Math.floor(scrollLeft);
        this.onNavDisabled(scrollLeft);
        // Breakpoint-/Orientierungswechsel vor der Init: Scrollbar-Platz neu bewerten
        this.reserveScrollbarSpace();
      });

      this.debouncedOnScroll = debounce(() => {
        this.onScroll();
      }, 100);

      this.setListeners();

      // Defer centerArrows so layout reads happen after paint, not during connectedCallback
      requestAnimationFrame(() => this.centerArrows());
    }
    // Platz fuer die horizontale OverlayScrollbars-Leiste (20px margin-bottom auf .theme-scroll--inner, app.css
    // Regel [data-overlayscrollbars-viewport*="overflowXScroll"]) schon VOR der Lazy-Init reservieren: sonst
    // waechst der Slider erst bei der Init um 20px und schiebt alle Sektionen darunter (Layout-Sprung).
    // OverlayScrollbars setzt overflowXScroll nur bei echtem Ueberlauf, deshalb messen statt pauschal
    // reservieren. Erst nach window.load messen, weil die Section-CSS deferred (media=print/onload) kommt.
    // Gemessen wird wie bei OverlayScrollbars das Viewport-Element (.theme-scroll--inner), nicht der Host: die absoluten
    // .flickity-nav-Pfeile (right:-23px) zaehlen zum Ueberlauf des Hosts, und Inner-Elemente mit eigenem overflow
    // (Blog-Slider) halten ihren Ueberlauf vom Host fern.
    reserveScrollbarSpace() {
      if (this._osInitialized || !this.viewport) { return; }
      if (document.readyState !== 'complete') {
        if (!this._osReserveQueued) {
          this._osReserveQueued = true;
          window.addEventListener('load', () => {
            this._osReserveQueued = false;
            this.reserveScrollbarSpace();
          }, { once: true });
        }
        return;
      }
      if (!this.isConnected) { return; }
      this.classList.toggle('theme-scroll--overflow-x', this.viewport.scrollWidth > this.viewport.clientWidth);
    }
    setSlides() {
      this.slides = this.querySelectorAll('.carousel__slide');
      this.slidesLength = this.slides.length;
      this.lastSlide = this.slides[this.slides.length - 1];
    }
    setListeners() {
      this.nav?.addEventListener('click', this.onNavClick.bind(this));
      this.viewport.addEventListener('scroll', this.debouncedOnScroll.bind(this));
      this.viewport.addEventListener('resize', this.debouncedOnScroll.bind(this));
      this.resizeObserver.observe(this.slides[0]);
    }
    onScroll() {
      // Read all layout values once, cache scrollLeft for reuse in onNavDisabled
      const scrollLeft = this.viewport.scrollLeft;
      this.reachedEnd = this.viewport.scrollWidth - this.viewport.offsetWidth <= Math.floor(scrollLeft);
      this.onNavDisabled(scrollLeft);
    }
    onNavClick(e) {
      if (!e.target.matches('.flickity-nav')) {
        return;
      }
      // Use cached scrollLeft to avoid a fresh layout read on click
      const scrollLeft = this.viewport.scrollLeft;
      if (e.target.name === 'next') {
        this.scrollPos = scrollLeft + this.slidesToScroll * this.slideWidth;
      } else if (e.target.name === 'prev') {
        this.scrollPos = scrollLeft - this.slidesToScroll * this.slideWidth;
      }
      this.viewport.scrollTo({
        left: this.scrollPos,
        behavior: "smooth"
      });
    }
    onNavDisabled(scrollLeft) {
      // Accept scrollLeft as a parameter to avoid re-reading it from the DOM
      if (scrollLeft === undefined) {
        scrollLeft = this.viewport.scrollLeft;
      }
      if (this.navPrev) {
        this.navPrev.disabled = scrollLeft === 0;
      }
      if (this.navNext) {
        this.navNext.disabled = this.reachedEnd;
      }
    }
    centerArrows() {
      const first_cell = this.slides[0];
      let image_height = 0;

      // Batch all reads before any writes
      const imageEl = first_cell.querySelector('.product-card-image') ||
        (first_cell.querySelector('.gallery--item') && this.slides[1]?.querySelector('.product-card-image'));

      if (imageEl) {
        image_height = imageEl.clientHeight;
      }

      if (image_height > 0) {
        let max_height = 0;
        this.slides.forEach((item) => {
          if (item.clientHeight > max_height) {
            max_height = item.clientHeight;
          }
        });

        // All writes after all reads
        if (max_height > image_height) {
          const difference = (max_height - image_height) / -2;
          const transform = 'translateY(' + difference + 'px)';
          this.navPrev.style.transform = transform;
          this.navNext.style.transform = transform;
        }
      }
    }
  }
  customElements.define('theme-scroll', ThemeScroll);
}

/**
 *  @class
 *  @function CollapsibleRow
 */
if (!customElements.get('collapsible-row')) {
  // https://css-tricks.com/how-to-animate-the-details-element/
  class CollapsibleRow extends HTMLElement {
    constructor() {
      super();

      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.content = this.querySelector('.collapsible__content');
      this.collapsed_mobile = this.classList.contains('accordion--collapsed-mobile');

      // Store the animation object (so we can cancel it if needed)
      this.animation = null;
      // Store if the element is closing
      this.isClosing = false;
      // Store if the element is expanding
      this.isExpanding = false;
    }
    connectedCallback() {
      this.setListeners();
    }
    setListeners() {
      if (this.initialized) { return; }
      this.querySelector('summary').addEventListener('click', (e) => this.onClick(e));

      this.initialized = true;
      if (this.collapsed_mobile) {
        window.addEventListener('resize', debounce(() => {
          this.details.open = window.innerWidth > 768;
        }, 100));
        this.details.open = window.innerWidth > 768;
      }
    }
    disconnectedCallback() {
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
      }
    }
    onClick(e) {
      // Stop default behaviour from the browser
      e.preventDefault();
      // Add an overflow on the <details> to avoid content overflowing
      this.details.style.overflow = 'hidden';

      // Check if the element is being closed or is already closed
      if (this.isClosing || !this.details.open) {
        this.open();
        // Check if the element is being openned or is already open
      } else if (this.isExpanding || this.details.open) {
        this.shrink();
      }
    }
    shrink() {
      // Set the element as "being closed"
      this.isClosing = true;

      // Store the current height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the height of the summary
      const endHeight = `${this.summary.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease'
      });

      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(false);
      // If the animation is cancelled, isClosing variable is set to false
      this.animation.oncancel = () => this.isClosing = false;
    }

    open() {
      // Apply a fixed height on the element
      this.details.style.height = `${this.details.offsetHeight}px`;
      // Force the [open] attribute on the details element
      this.details.open = true;
      // Wait for the next frame to call the expand function
      window.requestAnimationFrame(() => this.expand());
    }

    expand() {
      // Set the element as "being expanding"
      this.isExpanding = true;
      // Get the current fixed height of the element
      const startHeight = `${this.details.offsetHeight}px`;
      // Calculate the open height of the element (summary height + content height)
      const endHeight = `${this.summary.offsetHeight + this.content.offsetHeight}px`;

      // If there is already an animation running
      if (this.animation) {
        // Cancel the current animation
        this.animation.cancel();
      }

      // Start a WAAPI animation
      this.animation = this.details.animate({
        // Set the keyframes from the startHeight to endHeight
        height: [startHeight, endHeight]
      }, {
        duration: 250,
        easing: 'ease-out'
      });
      // When the animation is complete, call onAnimationFinish()
      this.animation.onfinish = () => this.onAnimationFinish(true);
      // If the animation is cancelled, isExpanding variable is set to false
      this.animation.oncancel = () => this.isExpanding = false;
    }

    onAnimationFinish(open) {
      // Set the open attribute based on the parameter
      this.details.open = open;
      // Clear the stored animation
      this.animation = null;
      // Reset isClosing & isExpanding
      this.isClosing = false;
      this.isExpanding = false;
      // Remove the overflow hidden and the fixed height
      this.details.style.height = this.details.style.overflow = '';

      this.dispatchEvent(new Event('animationend', { bubbles: false }));
    }
  }
  customElements.define('collapsible-row', CollapsibleRow);
}

/**
 *  @function addIdToRecentlyViewed
 */
function addIdToRecentlyViewed(handle) {
  if (!handle) {
    let product = document.querySelector('.thb-product-detail');

    if (product) {
      handle = product.dataset.handle;

    }
  }
  if (!handle) {
    return;
  }
  if (window.localStorage) {
    let recentIds = window.localStorage.getItem('recently-viewed');
    if (recentIds) {
      window.recentlyViewedIds = JSON.parse(recentIds);
    }
  }
  // Remove current product if already in recently viewed array
  var i = window.recentlyViewedIds.indexOf(handle);

  if (i > -1) {
    window.recentlyViewedIds.splice(i, 1);
  }

  // Add id to array
  window.recentlyViewedIds.unshift(handle);

  if (window.localStorage) {
    window.localStorage.setItem('recently-viewed', JSON.stringify(window.recentlyViewedIds));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  let scrollbarWidth = window.innerWidth - document.body.clientWidth;
  document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
});

/**
 *  Shopify standard events — adapters
 *  Translate the theme's internal events into shopify:* standard events
 *  in one place, instead of editing every dispatch site.
 */
document.addEventListener('cart:item-added', (event) => {
  const item = event.detail && event.detail.product;
  publishStandardEvent('CartLinesUpdateEvent', {
    action: 'add',
    lines: item && item.id ? [{ merchandiseId: item.id, quantity: item.quantity || 1 }] : undefined,
    promise: Promise.resolve({ cart: null })
  });
});
document.addEventListener('line-item:change:end', (event) => {
  const detail = event.detail || {};
  if (detail.cart && detail.cart.errors) {
    publishStandardEvent('CartErrorEvent', { message: detail.cart.errors });
    return;
  }
  publishStandardEvent('CartLinesUpdateEvent', {
    action: parseInt(detail.quantity, 10) === 0 ? 'remove' : 'update',
    promise: Promise.resolve({ cart: standardEventCart(detail.cart) })
  });
});
document.addEventListener('cart-drawer:open', () => {
  if (!(window.Shopify && window.Shopify.StandardEvents)) return;
  fetch(`${theme.routes.cart_url}.js`, { headers: { 'Accept': 'application/json' } })
    .then((response) => response.json())
    .then((cart) => {
      publishStandardEvent('CartViewEvent', { cart: standardEventCart(cart), context: 'dialog' });
    })
    .catch(() => {
      publishStandardEvent('CartViewEvent', { cart: null, context: 'dialog' });
    });
});
document.addEventListener('product:variant-error', (event) => {
  const detail = event.detail || {};
  publishStandardEvent('CartErrorEvent', {
    message: detail.message || detail.errors || ''
  });
});

/**
 *  Custom-Pixel-Bruecke: shopify:*-DOM-Events -> Shopify.analytics.publish
 *  Custom Pixels laufen sandboxed (Web Worker) und sehen DOM-Events nicht;
 *  publish() reicht sie in die Pixel-Sandbox weiter (Abo im GTM-Custom-Pixel
 *  via analytics.subscribe('theme_*')). Ohne Pixel-Abo wirkungslos, guarded —
 *  Analytics darf Commerce-Pfade nie brechen.
 */
var publishToPixel = function (name, payload) {
  if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
    try {
      Shopify.analytics.publish(name, payload || {});
    } catch (e) { /* no-op */ }
  }
};
document.addEventListener('shopify:cart:view', (e) => {
  // Payload-Zugriff tolerant: je nach standard-events-Version liegen die Felder
  // auf e.detail oder direkt auf dem Event-Objekt.
  const d = e.detail || e;
  const cart = d.cart || null;
  publishToPixel('theme_cart_viewed', {
    context: d.context || '',
    itemCount: (cart && cart.totalQuantity) || 0,
    total: (cart && cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.amount) || ''
  });
});
document.addEventListener('shopify:cart:lines-update', (e) => {
  const d = e.detail || e;
  publishToPixel('theme_cart_lines_update', { action: d.action || '' });
});
document.addEventListener('shopify:cart:error', (e) => {
  const d = e.detail || e;
  publishToPixel('theme_cart_error', { message: String(d.message || '').slice(0, 200) });
});
document.addEventListener('shopify:collection:update', (e) => {
  const d = e.detail || e;
  publishToPixel('theme_collection_filtered', {
    collectionId: (d.collection && d.collection.id) || '',
    filterCount: d.productFilters ? d.productFilters.length : 0,
    sortKey: d.sortKey || ''
  });
});
